import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import InvoiceUploader from './InvoiceUploader';
import UploadStatusList from './UploadStatusList';
import useUploadProgress from './useUploadProgress';
import { SETTLED_UPLOAD_KEYS, uploadStage } from './invoiceStatus';
import InvoiceList, { LIST_TABS, rowsForTab } from './InvoiceList';
import InvoiceReview from './InvoiceReview';
import DemoResetButton, { useDemoTools } from './DemoControls';
import useInvoiceLists from './useInvoiceLists';
import MailPanels from './MailPanels';
import InvoiceFilters, { NO_FILTERS, applyFilters } from './InvoiceFilters';
import { canDo, mayDo, useExperience } from '../experience/experience';

import { loadUploads, saveUploads } from './recentUploads';

const EXPORT_ROLES = ['org_owner', 'org_admin', 'manager', 'viewer', 'accountant'];     // staff never see prices (invoices.csv)

export default function InvoicesPage() {
  const { invoiceId, id } = useParams();
  if (invoiceId || id) return <InvoiceReview invoiceId={invoiceId || id} />;
  return <InvoicesHome />;
}

function InvoicesHome() {
  const lists = useInvoiceLists();
  const demo = useDemoTools();
  const exp = useExperience();
  const canUpload = canDo(exp, 'invoices.upload');
  const canDecide = canDo(exp, 'invoices.decide');
  const [uploads, setUploads] = useState(loadUploads);
  // /invoices?scan=1 (Today's "Scan invoice pages") opens the multi-page camera once: the flag is read on arrival and
  // dropped from the address, so a refresh or a Back does not open it again. /invoices?tab=disputed opens that tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const picked = LIST_TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : null;
  const setPicked = (id) => { const next = new URLSearchParams(searchParams); next.set('tab', id); setSearchParams(next, { replace: true }); };
  const [openScan] = useState(() => searchParams.get('scan') === '1');
  useEffect(() => {
    // Not while the page is still settling: it can remount then, and the flag must survive to be read.
    if (!searchParams.has('scan') || exp.status !== 'ready') return;
    const next = new URLSearchParams(searchParams);
    next.delete('scan');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, exp.status]);
  // The search and dates narrow what the tabs show; the upload progress still reads the full lists (lists.byDoc).
  const [filters, setFilters] = useState(NO_FILTERS);
  const shownByState = useMemo(() => Object.fromEntries(
    Object.entries(lists.byState).map(([state, rows]) => [state, applyFilters(rows, filters)])), [lists.byState, filters]);
  const firstFull = LIST_TABS.find((t) => rowsForTab(shownByState, t.id).length > 0);
  const tab = picked || firstFull?.id || 'needs_review';

  useEffect(() => {
    saveUploads(uploads);
  }, [uploads]);

  const progress = useUploadProgress(uploads, lists.byDoc);
  const unresolved = uploads.some((u) => u.documentId && !lists.byDoc[u.documentId]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!unresolved) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);   // the elapsed time on a Reading row counts live
    return () => clearInterval(t);
  }, [unresolved]);

  // A row whose file is posted, rejected or gone needs nothing more from the person: it clears itself shortly,
  // so a finished file is not left looking busy among the ones still being read.
  const clearing = useRef({});
  useEffect(() => {
    uploads.forEach((u) => {
      if (clearing.current[u.key]) return;
      if (!SETTLED_UPLOAD_KEYS.includes(uploadStage(u, lists.byDoc, Date.now(), progress).key)) return;
      clearing.current[u.key] = setTimeout(() => setUploads((prev) => prev.filter((x) => x.key !== u.key)), 8000);
    });
  }, [uploads, lists.byDoc, progress]);
  // Timers outlive a refresh (a refresh must not restart the 8 seconds); they stop only when the page goes.
  useEffect(() => () => Object.values(clearing.current).forEach(clearTimeout), []);

  // Mark rows as polled once a refresh has run after they were sent, so "Uploaded" becomes "Reading".
  useEffect(() => {
    setUploads((prev) => (prev.some((u) => !u.polled && u.documentId) ? prev.map((u) => ({ ...u, polled: true })) : prev));
  }, [lists.tick]);

  const onUploads = (rows) => {
    setUploads((prev) => [...rows, ...prev].slice(0, 20));
    lists.refresh();
    demo.refresh();
  };

  const afterReset = () => {
    setUploads([]);
    lists.refresh();
    demo.refresh();
  };

  return (
    <main className="min-h-screen bg-surface-muted" data-testid="invoices-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-ink">Invoices</h1>
            <p className="text-ink-muted mt-1">Upload a supplier invoice, check what was read, then approve the new prices.</p>
          </div>
          <DemoResetButton info={demo.info} onDone={afterReset} />
        </header>
        {canUpload && <InvoiceUploader onUploads={onUploads} openScan={openScan} />}
        <UploadStatusList uploads={uploads} invoicesByDoc={lists.byDoc} progress={progress} now={now}
          onDismiss={(key) => setUploads((prev) => prev.filter((u) => u.key !== key))} />
        {/* canDo() says yes while the person's permissions are still loading; these fetch manager-only data, so they wait. */}
        {canDecide && exp.status === 'ready' && <MailPanels tick={lists.tick} onChanged={lists.refresh} />}
        <InvoiceFilters filters={filters} onChange={setFilters} tab={LIST_TABS.find((t) => t.id === tab)}
          shown={rowsForTab(shownByState, tab).length} canExport={mayDo(exp, EXPORT_ROLES) && !LIST_TABS.find((t) => t.id === tab)?.where} />
        <InvoiceList byState={shownByState} tab={tab} onTab={setPicked} loaded={lists.loaded} errors={lists.errors} onRetry={lists.refresh} />
      </div>
    </main>
  );
}
